'use client'

/**
 * PostHog user identification and page-view tracking for the admin dashboard.
 * Rendered inside DashboardShell, within the AuthProvider's Suspense boundary.
 * - Identifies the user in PostHog when the auth session loads.
 * - Associates the user with their active tenant (PostHog group).
 * - Fires $pageview on every route change.
 */

import { useEffect, useRef, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import posthog from 'posthog-js'
import { useAuth } from './auth/AuthProvider'
import { isPostHogReady, onPostHogReady } from '@auctogon/admin-isomorphic/isomorphic/posthog-state'

export default function PostHogUserIdentify() {
  const { user } = useAuth()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastUserId = useRef<string | null>(null)
  // Refs so the onPostHogReady callback always reads the current URL, not the
  // mount-time URL. In the async init path (PostHog not yet ready at mount),
  // the callback fires later — by which point the user may have navigated.
  const pathnameRef = useRef(pathname)
  const searchParamsRef = useRef(searchParams)
  useEffect(() => { pathnameRef.current = pathname }, [pathname])
  useEffect(() => { searchParamsRef.current = searchParams }, [searchParams])
  // React fires effects bottom-up (children before parents), so PostHogInit's
  // effect may not have run yet on the first render. Track readiness locally
  // and re-run identify/pageview once PostHog becomes ready.
  const [isReady, setIsReady] = useState(isPostHogReady)

  useEffect(() => {
    if (isReady) return
    return onPostHogReady(() => {
      setIsReady(true)
      // Fire the initial pageview here rather than in the navigation effect below.
      // This avoids a double-fire: if isReady were in the navigation effect's deps,
      // it would trigger a $pageview when isReady flips to true AND again on the
      // next navigation. Refs are used so we read the current URL at callback-fire
      // time, not the mount-time URL (which may be stale in the async init path).
      const url = pathnameRef.current + (searchParamsRef.current.toString() ? `?${searchParamsRef.current.toString()}` : '')
      posthog.capture('$pageview', { $current_url: url })
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]) // pathname/searchParams read via refs — always current at callback-fire time

  // Identify user when they log in or change
  useEffect(() => {
    if (!isReady) return
    if (user && user.id !== lastUserId.current) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
      })
      lastUserId.current = user.id
    } else if (!user && lastUserId.current) {
      posthog.reset()
      lastUserId.current = null
    }
  }, [user, isReady])

  // Fire $pageview on route changes (not on SDK init — the initial pageview is
  // fired in the onPostHogReady callback above to avoid double-firing).
  useEffect(() => {
    if (!isReady) return
    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '')
    posthog.capture('$pageview', { $current_url: url })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]) // intentionally excludes isReady — pageview fires on navigation only

  return null
}
